import { useCallback, useEffect, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import {
  Bold,
  Bucket,
  Heading1,
  Heading2,
  Heading3,
  Image,
  Pencil,
} from '@factorialco/f0-react/icons/app'
import { AnimatePresence, motion } from 'framer-motion'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react'

import { useDirectUpload } from 'modules/core/hooks/useDirectUpload'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { AttachedFile } from 'modules/trainings/lib/content/AttachedFile'
import {
  ContentModule,
  DEFAULT_BACKGROUND_COLOR,
  FONT_SIZES,
  FONT_WEIGHTS,
} from 'modules/trainings/lib/content/Module'

import './index.styles.css'

const ALL_COLORS = [
  DEFAULT_BACKGROUND_COLOR,
  '#07a0ab',
  '#068089',
  '#056e76',
  '#5596f6',
  '#497fd0',
  '#3a65a6',
  '#8d61f5',
  '#7650ce',
  '#6143a8',
  '#5052f7',
  '#4447cf',
  '#3739a9',
  '#f0519e',
  '#c94585',
  '#a3386e',
  '#cb6788',
  '#ab5470',
  '#8b465d',
  '#e61942',
  '#c11538',
  '#9c112f',
  '#be7e50',
  '#9f6841',
  '#825535',
  '#f6a61e',
  '#cd8b18',
  '#a56f12',
  '#94c945',
  '#7eaa3c',
  '#5e7d2c',
]

const DOCK_ANIMATION = {
  initial: { opacity: 0, scaleX: 0.8 },
  animate: { opacity: 1, scaleX: 1 },
  exit: { opacity: 0, scaleX: 0.8 },
  transition: { duration: 0.15 },
} as const

type EditDockProps = {
  contentItem: ContentModule
  onContentChange?: () => void
  trainingId: string
}

type ColorSwatchProps = {
  color: string
  isSelected: boolean
  onSelect: (color: string) => void
}

const ColorSwatch = ({ color, isSelected, onSelect }: ColorSwatchProps) => (
  <button
    type="button"
    className={`editDock_colorSwatch${isSelected ? ' editDock_colorSwatchSelected' : ''}`}
    style={{ backgroundColor: color }}
    onClick={() => onSelect(color)}
    aria-label={i18n.t('trainings.content.edit_dock.background_color_swatch', { color })}
  />
)

type ColorPickerProps = {
  selectedColor: string
  onSelect: (color: string) => void
}

const DRAG_THRESHOLD_PX = 3

const ColorPicker = ({ selectedColor, onSelect }: ColorPickerProps) => {
  const stripRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const dragStateRef = useRef({ startX: 0, startScrollLeft: 0 })
  const hasDraggedRef = useRef(false)

  useEffect(() => {
    const stripEl = stripRef.current
    if (!stripEl) return

    const handleWheel = (event: WheelEvent) => {
      const delta =
        Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY
      if (delta === 0) return

      event.preventDefault()
      stripEl.scrollBy({ left: delta, behavior: 'auto' })
    }

    stripEl.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      stripEl.removeEventListener('wheel', handleWheel)
    }
  }, [])

  const handleMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !stripRef.current) return

    event.preventDefault()
    dragStateRef.current = {
      startX: event.clientX,
      startScrollLeft: stripRef.current.scrollLeft,
    }
    hasDraggedRef.current = false
    setIsDragging(true)
  }, [])

  const handleClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!hasDraggedRef.current) return

    event.preventDefault()
    event.stopPropagation()
    hasDraggedRef.current = false
  }, [])

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (event: MouseEvent) => {
      if (!stripRef.current) return

      const deltaX = event.clientX - dragStateRef.current.startX

      if (!hasDraggedRef.current) {
        if (Math.abs(deltaX) <= DRAG_THRESHOLD_PX) return

        hasDraggedRef.current = true
        dragStateRef.current = {
          startX: event.clientX,
          startScrollLeft: stripRef.current.scrollLeft,
        }
      }

      const effectiveDeltaX = event.clientX - dragStateRef.current.startX
      stripRef.current.scrollLeft = dragStateRef.current.startScrollLeft - effectiveDeltaX
    }

    const stopDragging = () => {
      setIsDragging(false)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', stopDragging)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', stopDragging)
    }
  }, [isDragging])

  return (
    <Box className="editDock_colorPicker">
      <div
        className="editDock_colorStrip"
        ref={stripRef}
        onMouseDown={handleMouseDown}
        onClickCapture={handleClickCapture}
      >
        {ALL_COLORS.map((color) => (
          <ColorSwatch
            key={color}
            color={color}
            isSelected={selectedColor === color}
            onSelect={onSelect}
          />
        ))}
      </div>
    </Box>
  )
}

export const EditDock = observer(({ contentItem, onContentChange, trainingId }: EditDockProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showColors, setShowColors] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dockRef = useRef<HTMLDivElement>(null)
  const { upload: directUpload } = useDirectUpload({
    resourceType: 'Trainings::Training',
    resourceId: trainingId,
  })

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setShowColors(false)
  }, [])

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (dockRef.current && e.target instanceof Node && !dockRef.current.contains(e.target)) {
        handleClose()
      }
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, handleClose])

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const showUploadError = () =>
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })

    try {
      const result = await directUpload(file)
      if (result.type === 'success') {
        runInAction(() => {
          contentItem.thumbnail = AttachedFile.fromUpload(file, result.signedId)
        })
        onContentChange?.()
      } else {
        showUploadError()
      }
    } catch {
      showUploadError()
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSelectColor = (color: string) => {
    runInAction(() => {
      contentItem.backgroundColor = color
    })
    onContentChange?.()
    setShowColors(false)
  }

  const handleFontSize = (size: string) => {
    runInAction(() => {
      contentItem.fontSize = size
    })
    onContentChange?.()
  }

  const handleToggleBold = () => {
    runInAction(() => {
      contentItem.fontWeight =
        contentItem.fontWeight === FONT_WEIGHTS.BOLD ? FONT_WEIGHTS.NORMAL : FONT_WEIGHTS.BOLD
    })
    onContentChange?.()
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="editDock_hiddenInput"
      />

      <Box className="editDock_barWrapper" ref={dockRef}>
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="toolbar"
              className="editDock_toolbar"
              style={{ transformOrigin: 'right center' }}
              {...DOCK_ANIMATION}
            >
              <F0Button
                icon={Bucket}
                label={i18n.t('trainings.content.edit_dock.background_color')}
                hideLabel
                variant="outline"
                size="md"
                onClick={() => setShowColors((prev) => !prev)}
              />
              <F0Button
                icon={Image}
                label={i18n.t('trainings.content.edit_dock.upload_image')}
                hideLabel
                variant="outline"
                size="md"
                onClick={() => fileInputRef.current?.click()}
              />
              <Box className="editDock_separator" />

              <F0Button
                icon={Heading3}
                label={i18n.t('trainings.content.edit_dock.font_size_small')}
                hideLabel
                variant={contentItem.fontSize === FONT_SIZES.SMALL ? 'primary' : 'outline'}
                size="md"
                onClick={() => handleFontSize(FONT_SIZES.SMALL)}
              />
              <F0Button
                icon={Heading2}
                label={i18n.t('trainings.content.edit_dock.font_size_medium')}
                hideLabel
                variant={contentItem.fontSize === FONT_SIZES.MEDIUM ? 'primary' : 'outline'}
                size="md"
                onClick={() => handleFontSize(FONT_SIZES.MEDIUM)}
              />
              <F0Button
                icon={Heading1}
                label={i18n.t('trainings.content.edit_dock.font_size_large')}
                hideLabel
                variant={contentItem.fontSize === FONT_SIZES.LARGE ? 'primary' : 'outline'}
                size="md"
                onClick={() => handleFontSize(FONT_SIZES.LARGE)}
              />

              <Box className="editDock_separator" />

              <F0Button
                icon={Bold}
                label={
                  contentItem.fontWeight === FONT_WEIGHTS.BOLD
                    ? i18n.t('trainings.content.edit_dock.unbold')
                    : i18n.t('trainings.content.edit_dock.bold')
                }
                hideLabel
                variant={contentItem.fontWeight === FONT_WEIGHTS.BOLD ? 'primary' : 'outline'}
                size="md"
                onClick={handleToggleBold}
              />
            </motion.div>
          ) : (
            <motion.div key="trigger" className="editDock_triggerWrapper" {...DOCK_ANIMATION}>
              <F0Button
                icon={Pencil}
                label={i18n.t('trainings.content.edit_dock.edit_style')}
                hideLabel
                variant="neutral"
                size="md"
                onClick={() => setIsOpen(true)}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {showColors && (
          <ColorPicker selectedColor={contentItem.backgroundColor} onSelect={handleSelectColor} />
        )}
      </Box>
    </>
  )
})
